/**
 * Two-factor sign-in: Supabase MFA with authenticator-app (TOTP) codes.
 *
 * A user who has a verified factor has not finished signing in until they
 * enter a code from it: until then their session is `aal1`. Middleware sends
 * such a session's page loads to MFA_PAGE and answers its API calls with 401
 * `mfa_required`; the bearer-token path (the phone app) refuses it in
 * `withBearerAuth`; and `docs/mfa-required-rls.sql` refuses it in the
 * database. Users without a factor are never affected.
 *
 * Edge-safe and browser-safe: no Node or server-only imports.
 */

export const MFA_PAGE = '/auth/mfa';

/** The `code` of the 401 middleware sends an unverified session's API call. */
export const MFA_REQUIRED = 'mfa_required';

export const MFA_REQUIRED_MESSAGE =
  'Enter the code from your authenticator app to continue.';

type FactorLike = { status?: string | null };

export function hasVerifiedFactor(
  user: { factors?: FactorLike[] | null } | null | undefined,
): boolean {
  return !!user?.factors?.some((factor) => factor.status === 'verified');
}

/**
 * The `aal` claim of a Supabase access token. Decodes without verifying: call
 * it only on a token Supabase Auth has just accepted (`getUser`).
 */
export function tokenAal(
  accessToken: string | null | undefined,
): string | null {
  const payload = accessToken?.split('.')[1];
  if (!payload) return null;
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '='));
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    const claims = JSON.parse(new TextDecoder().decode(bytes));
    return typeof claims?.aal === 'string' ? claims.aal : null;
  } catch {
    return null;
  }
}

/** True when this user's session still has to pass the code step. */
export function needsMfaCode(
  user: { factors?: FactorLike[] | null } | null | undefined,
  accessToken: string | null | undefined,
): boolean {
  return hasVerifiedFactor(user) && tokenAal(accessToken) !== 'aal2';
}

export type MfaErrorReason =
  | 'invalid_code'
  | 'expired'
  | 'rate_limited'
  | 'failed';

/** Why Supabase refused a code, for the message and the `Auth Error` event. */
export function mfaErrorReason(
  error: { code?: string | null; status?: number | null } | null | undefined,
): MfaErrorReason {
  if (error?.status === 429 || error?.code === 'over_request_rate_limit') {
    return 'rate_limited';
  }
  if (error?.code === 'mfa_verification_failed') return 'invalid_code';
  if (error?.code === 'mfa_challenge_expired') return 'expired';
  return 'failed';
}

/**
 * What a typed or pasted code becomes: its digits, at most six. Arabic-Indic
 * and full-width digits (Arabic and CJK keyboards) count as digits.
 */
export function normalizeMfaCode(value: string): string {
  return value
    .replace(/[٠-٩۰-۹０-９]/g, (char) => {
      const code = char.charCodeAt(0);
      const zero = code >= 0xff10 ? 0xff10 : code >= 0x06f0 ? 0x06f0 : 0x0660;
      return String(code - zero);
    })
    .replace(/\D/g, '')
    .slice(0, 6);
}

/**
 * Where to go after the code step: a path on this site, never another origin
 * (`//evil.com`, `/\evil.com`) or a scheme.
 */
export function safeNextPath(value: unknown, fallback = '/dashboard'): string {
  const next = Array.isArray(value) ? value[0] : value;
  if (typeof next !== 'string' || !next.startsWith('/')) return fallback;
  if (next.startsWith('//') || next.startsWith('/\\')) return fallback;
  // Browsers drop tabs and newlines from URLs: "/\t/evil.com" is "//evil.com".
  if ([...next].some((char) => char.charCodeAt(0) < 0x20)) return fallback;
  // The code page itself would send the user round in a circle.
  if (/^(?:\/[a-z]{2})?\/auth\/mfa(?:[/?#]|$)/.test(next)) return fallback;
  return next;
}

const LOCALE_PREFIX = /^\/(?:fr|es|de|ko|ja|zh|it|th|ar|pt|ms|vi)(?=\/|$)/;

// Pages an unverified session may still open: the code page and the screens
// that create a session (signing in again, or as someone else, an emailed
// link landing on /auth/verify, asking for a reset link). Gating those would
// ask for the old session's code before the link makes a new one that needs
// its own. /auth/reset-password stays behind the code.
const OPEN_PAGES = new Set([
  MFA_PAGE,
  '/auth/callback',
  '/auth/confirm',
  '/auth/verify',
  '/auth/auth-code-error',
  '/auth/forgot-password',
  '/login',
  '/signup',
]);

// Files, not pages (fonts, scripts, media in public/). The matcher already
// skips images.
const STATIC_FILE =
  /\.(?:css|js|mjs|map|json|txt|xml|ico|woff2?|ttf|otf|eot|mp3|mp4|webm|wav|avif|pdf|webmanifest)$/i;

/**
 * What middleware does with a request from a session that still needs its
 * code: let it through, send the page to the code step, or refuse the API
 * call. `navigation` is a top-level browser load (an OAuth hop through an
 * /api route), which is redirected rather than answered with JSON.
 */
export function mfaGateAction(
  pathname: string,
  { navigation = false }: { navigation?: boolean } = {},
): 'pass' | 'redirect' | 'reject' {
  const path = pathname.replace(LOCALE_PREFIX, '') || '/';
  if (path.startsWith('/_next/') || path.startsWith('/__nextjs')) {
    return 'pass';
  }
  if (path === '/api' || path.startsWith('/api/')) {
    return navigation ? 'redirect' : 'reject';
  }
  if (OPEN_PAGES.has(path.replace(/\/$/, '') || '/')) return 'pass';
  if (STATIC_FILE.test(path)) return 'pass';
  return 'redirect';
}
